import { Component, OnInit } from '@angular/core';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySliderChange as MatSliderChange } from '@angular/material/legacy-slider';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogDeleteGenericComponent } from '@app/shared/components/dialog-delete-generic/dialog-delete-generic.component';
import { NotificationType } from '@app/shared/interfaces';
import { Filter } from '@app/shared/models/filter';
import { PropertiesService } from '@app/shared/services/properties/properties.service';
import { TasksService } from '@app/shared/services/tasks/tasks.service';
import { TeamService } from '@app/shared/services/team/team.service';
import { TitleService } from '@app/shared/services/title/title.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';

@Component({
  standalone: false,
  selector: 'sbnb-task-detail',
  templateUrl: './task-detail.component.html',
  styleUrls: ['./task-detail.component.scss'],
})
export class TaskDetailComponent implements OnInit {
  taskUuid: any;
  task: any;
  meta: any;
  loading: boolean;
  saveStatus: number;
  possibleProperties: any[];
  possibleTeammates: any[];
  durationIndex: any;
  numListOptionsForSearchBar = 10;
  copying: boolean;
  deleting: boolean;
  searchCriteria: string;
  taskEntry: string;

  constructor(
    private taskService: TasksService,
    private route: ActivatedRoute,
    private propertiesService: PropertiesService,
    private teamService: TeamService,
    private dialog: MatDialog,
    private router: Router,
    private toast: ToastNotificationsService,
    private titleService: TitleService
  ) { }

  ngOnInit() {
    this.titleService.manuallySet('Task rule detail');
    this.taskUuid = this.route.snapshot.paramMap.get('id');

    if (this.taskUuid) {
      this.getTask();
    } else {
      this.router.navigate(['/operations/task-rules']);
    }
  }

  getProperties() {
    this.propertiesService.getScopingProperties([], null, null, 5000, false).subscribe((res) => {
      this.possibleProperties = res.data;
    });

    const filters: Filter[] = [
      {
        uuid: 'notused',
        id: 'teammates-services',
        label: 'Services',
        loading: false,
        lockable: false,
        selectedValue: { key: this.task.service_id, label: 'notusedhere' },
        type: 'list',
        values: 'notused',
      },
    ];

    this.teamService.getTeamMembers(filters, null, null, 0, false).subscribe((res: any) => {
      this.possibleTeammates = res.data;
    });
  }

  getTask() {
    this.taskService.getTaskRule(this.taskUuid).subscribe((res) => {
      if (res.error) {
        this.router.navigate(['/operations/task-rules']);
        return;
      }

      this.durationIndex = res.meta?.durations.findIndex((x) => x.value === res.data.duration_seconds);
      res.data.duration = res.data.duration_seconds;

      this.task = res.data;

      this.meta = res.meta;

      this.titleService.manuallySet(this.task.name);

      this.getProperties();
    });
  }

  triggerSave() {
    this.saveStatus = 1;

    this.taskService.updateTaskRule(this.taskUuid, this.task).subscribe((res) => {
      if (res.error) {
        this.saveStatus = -1;
        return;
      }

      this.saveStatus = 2;
      setTimeout(() => {
        this.saveStatus = null;
      }, 2000);
    });
  }

  durationSliderUpdated(event: MatSliderChange) {
    this.durationIndex = event.value;

    this.task.duration = this.meta.durations[this.durationIndex].value;
    this.triggerSave();
  }

  allPropertyChange(event: MatCheckboxChange) {
    this.task.all_properties = event.checked;
    if (this.task.relative_to === 'recurring' && event.checked) {
      this.possibleProperties.forEach((property) => {
        this.task.properties.push(property.id);
      });
    } else {
      this.task.properties = [];
    }
    this.triggerSave();
  }

  propertyCheckboxChange(property, event: MatCheckboxChange) {
    if (event.checked) {
      if (this.task.properties.indexOf(property.id) === -1) {
        this.task.properties.push(property.id);
        this.triggerSave();
      }
    } else {
      const index = this.task.properties.findIndex((r) => r === property.id);

      if (index !== -1) {
        this.task.properties.splice(index, 1);
        this.triggerSave();
      }
    }
  }

  assignmentChange(event: any) {
    if (event === 'teammate' && !this.task.teammate_uuid) {
      return;
    }

    this.triggerSave();
  }

  timingUpdated(event) {
    this.triggerSave();
  }

  goBack() {
    this.router.navigate(['/operations/task-rules']);
  }

  activateOrDeactivateRule(event: MatCheckboxChange) {
    this.task.active = event.checked;
    this.saveStatus = 1;

    this.taskService.activateOrDeactivateTaskRule(this.task.uuid, this.task.active).subscribe((res: any) => {
      if (res && res.error) {
        this.task.active = !this.task.active;
        this.saveStatus = -1;

        this.toast.open(`Oops! We ran into an error updating the task rule`, 'Dismiss', NotificationType.Error);
      } else {
        this.saveStatus = 2;

        setTimeout(() => {
          this.saveStatus = null;
        }, 2000);
      }
    });
  }

  copyRuleset() {
    this.copying = true;
    this.taskService.copyTaskRule(this.taskUuid).subscribe((res) => {
      this.copying = false;
      if (res.data) {
        this.router.navigate(['/operations/task-rules/task/' + res.data.uuid]);
      }
    });
  }

  deletePrompt() {
    const dialogRef = this.dialog.open(DialogDeleteGenericComponent, {
      width: '720px',
      data: {
        name: 'Task Rule',
        identifier: this.task.name,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.deleteRuleset();
      }
    });
  }

  deleteRuleset() {
    this.deleting = true;
    this.taskService.deleteTaskRule(this.taskUuid).subscribe((res) => {
      this.deleting = false;
      if (res.success) {
        this.router.navigate(['/operations/task-rules']);
      }
    });
  }

  selectedTeamMember(member: any) {
    this.task.teammate_uuid = member.uuid;
    this.task.teammate_name = member.name;

    this.triggerSave();
  }

  saveTask() {
    if (this.taskEntry !== '') {
      this.task.task_list.push({
        id: this.task.task_list.length + 1,
        entry: this.taskEntry,
      });
      this.taskEntry = '';
      this.triggerSave();
    }
  }

  updateTask() {
    this.triggerSave();
  }

  deleteTask(id: number) {
    const index = this.task.task_list.findIndex((task) => task.id === id);
    if (index === -1) {
      return;
    }
    this.task.task_list.splice(index, 1);

    this.triggerSave();
  }
}
